/**
 * Serviço centralizado para operações de metadados do CorelDRAW
 * @module MetadataService
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal } from '../../../lib/terminalLogger';
import { LogCategory } from '../../../config/logging';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';
import { getActionTimeout } from '../timeoutService';
import type {
  MetadataScope,
  UpsertItem,
  CollectResponse,
  UpsertResponse,
  DeleteResponse,
  FullMetadataResponse,
  ObjetoCorel,
  PaginaCorel,
  MetadataCollectionResult
} from './metadata.types';

/**
 * Informação de progresso enviada pelo C# durante coleta de metadados
 */
export interface MetadataProgress {
  /** Percentual completo (0-100) */
  percentComplete: number;
  /** Mensagem descritiva do progresso */
  message: string;
  /** Número atual de objetos processados */
  current: number;
  /** Total de objetos a processar */
  total: number;
  /** Label da etapa atual */
  label?: string;
  /** Nome da action */
  action?: string;
}

/**
 * Opções para coleta de metadados com suporte a progresso
 */
export interface CollectOptions {
  /** Escopo da coleta: document ou selection */
  scope?: MetadataScope;
  /** IDs específicos para coletar (opcional) */
  staticIds?: string[];
  /** Callback chamado a cada atualização de progresso do C# */
  onProgress?: (progress: MetadataProgress) => void;
  /** Timeout em ms (padrão: 2 minutos, resetado a cada progress_update) */
  timeoutMs?: number;
}

/**
 * Constantes para retry
 */
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 500;
// Timeout busca do painel admin (fallback: 1h), com incremento para operações grandes
const getMetadataBaseTimeout = () => getActionTimeout('metadados-objetos');
const METADATA_TIMEOUT_INCREMENT = 30 * 1000; // 30 segundos

/**
 * Constantes para UX de progresso
 * Garante que o usuário consiga visualizar a barra de progresso mesmo em operações rápidas
 */
const PROGRESS_MIN_SHOW_TIME_MS = 400; // Tempo mínimo antes de mostrar primeiro progresso
const PROGRESS_MIN_CLOSE_DELAY_MS = 300; // Tempo mínimo após 100% antes de fechar

/**
 * Erros customizados para operações de metadados
 */
export class MetadataError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalMessage?: string
  ) {
    super(message);
    this.name = 'MetadataError';
  }
}

export class NoActiveDocumentError extends MetadataError {
  constructor(originalMessage?: string) {
    super(
      'Nenhum documento ativo no CorelDRAW. Por favor, abra um documento antes de continuar.',
      'NO_ACTIVE_DOCUMENT',
      originalMessage
    );
    this.name = 'NoActiveDocumentError';
  }
}

export class NoSelectionError extends MetadataError {
  constructor(originalMessage?: string) {
    super(
      'Nenhum objeto selecionado no CorelDRAW. Por favor, selecione pelo menos um objeto antes de continuar.',
      'NO_SELECTION',
      originalMessage
    );
    this.name = 'NoSelectionError';
  }
}

export class MetadataGenericError extends MetadataError {
  constructor(message: string, originalMessage?: string) {
    super(
      message || 'Erro ao coletar metadados do CorelDRAW. Verifique se o documento está aberto e tente novamente.',
      'METADATA_GENERIC_ERROR',
      originalMessage
    );
    this.name = 'MetadataGenericError';
  }
}

/**
 * Serviço centralizado para todas as operações de metadados
 */
export class MetadataService {
  /**
   * Coleta metadados do documento ou seleção
   *
   * @param scopeOrOptions - Escopo da coleta (document ou selection) ou objeto de opções completo
   * @param staticIds - StaticIDs específicos para coletar (opcional, ignorado se scopeOrOptions for objeto)
   * @returns Promise com resposta da coleta
   *
   * @example
   * // Uso simples (compatível com versão anterior)
   * const result = await MetadataService.collect('selection');
   *
   * @example
   * // Uso com progresso
   * const result = await MetadataService.collect({
   *   scope: 'selection',
   *   onProgress: (progress) => {
   *     console.log(`${progress.percentComplete}% - ${progress.message}`);
   *   }
   * });
   */
  static async collect(
    scopeOrOptions: MetadataScope | CollectOptions = 'document',
    staticIds?: string[]
  ): Promise<CollectResponse> {
    // Normalizar parâmetros para suportar ambas as assinaturas
    const options: CollectOptions = typeof scopeOrOptions === 'object'
      ? scopeOrOptions
      : { scope: scopeOrOptions, staticIds };

    const scope = options.scope || 'document';
    const ids = options.staticIds;
    const onProgress = options.onProgress;
    const timeoutMs = options.timeoutMs || getMetadataBaseTimeout();

    sendToTerminal('📤 [MetadataService:collect] ENTRADA', {
      scope,
      staticIdsCount: ids?.length,
      hasProgressCallback: !!onProgress,
      timeoutMs
    }, LogCategory.BUSINESS);
    
    debugLog('INFO', {
      scope: 'MetadataService.collect',
      operation: 'collect',
      scopeType: scope,
      staticIdsCount: ids?.length,
      hasProgressCallback: !!onProgress
    });

    try {
      const payload: any = {
        operation: 'collect',
        scope
      };

      if (ids && ids.length > 0) {
        payload.staticIds = ids;
      }

      sendToTerminal('📤 [MetadataService:collect] Enviando comando para CorelDRAW...', {
        payload: { operation: payload.operation, scope: payload.scope },
        hasProgressCallback: !!onProgress
      }, LogCategory.BUSINESS);

      const response = await this.sendMetadataCommand<any>(payload, timeoutMs, onProgress);
      
      sendToTerminal('📥 [MetadataService:collect] Resposta recebida do CorelDRAW', {
        status: response.status,
        hasResult: !!response.result,
        resultSucesso: response.result?.sucesso
      }, LogCategory.BUSINESS);

      // ✅ Tratar erros no nível da resposta (status: "error" ou "erro")
      if (response.status === 'error' || response.status === 'erro') {
        const errorMessage = response.message || 'Erro ao coletar metadados';
        sendToTerminal('❌ [MetadataService:collect] Erro no nível da resposta', {
          status: response.status,
          message: errorMessage
        }, LogCategory.ERROR);
        throw new MetadataGenericError(errorMessage);
      }

      const result = response.result;
      
      // ✅ Tratar erros no nível do result (status: "ok" mas sucesso: false)
      if (!result || !result.sucesso) {
        const errorMessage = result?.mensagem || 'Operação de coleta falhou';
        const errorMessageLower = errorMessage.toLowerCase();
        
        sendToTerminal('⚠️ [MetadataService:collect] Operação falhou (sucesso: false)', {
          mensagem: errorMessage,
          count: result?.count || 0,
          durationMs: result?.durationMs || 0
        }, LogCategory.ERROR);
        
        // ✅ Detectar erros específicos baseado na mensagem do backend
        if (errorMessageLower.includes('nenhum documento ativo') || 
            errorMessageLower.includes('documento não ativo')) {
          const error = new NoActiveDocumentError(errorMessage);
          sendToTerminal('❌ [MetadataService:collect] Erro detectado: NO_ACTIVE_DOCUMENT', {
            code: error.code,
            message: error.message,
            originalMessage: error.originalMessage
          }, LogCategory.ERROR);
          throw error;
        } else if (errorMessageLower.includes('nenhuma seleção') || 
                   errorMessageLower.includes('seleção vazia') ||
                   errorMessageLower.includes('nenhum objeto selecionado')) {
          const error = new NoSelectionError(errorMessage);
          sendToTerminal('❌ [MetadataService:collect] Erro detectado: NO_SELECTION', {
            code: error.code,
            message: error.message,
            originalMessage: error.originalMessage
          }, LogCategory.ERROR);
          throw error;
        } else {
          // Erro genérico - usar mensagem do backend
          const error = new MetadataGenericError(errorMessage, errorMessage);
          sendToTerminal('❌ [MetadataService:collect] Erro detectado: METADATA_GENERIC_ERROR', {
            code: error.code,
            message: error.message,
            originalMessage: error.originalMessage
          }, LogCategory.ERROR);
          throw error;
        }
      }

      debugLog('INFO', {
        scope: 'MetadataService.collect',
        success: true,
        count: result.count || result.objetos?.length || 0,
        duration: result.durationMs
      });

      return {
        sucesso: result.sucesso,
        objetos: result.objetos || [],
        count: result.count || result.objetos?.length || 0,
        durationMs: result.durationMs,
        mensagem: result.mensagem, // Mensagem do C# (ex: "Nenhuma seleção.")
        erro: result.erro // Mantido para compatibilidade
      };

    } catch (error) {
      // ✅ Preservar erros customizados (MetadataError e subclasses)
      if (error instanceof MetadataError) {
        // Log já foi feito antes de lançar o erro, apenas re-lançar
        throw error;
      }
      
      // ✅ Converter outros erros para MetadataGenericError
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      sendToTerminal('❌ [MetadataService:collect] Erro não tratado convertido para MetadataGenericError', {
        errorMessage,
        errorType: error instanceof Error ? error.constructor.name : typeof error
      }, LogCategory.ERROR);
      
      debugLog('ERROR', {
        scope: 'MetadataService.collect',
        errorMessage
      });

      throw new MetadataGenericError(errorMessage, errorMessage);
    }
  }

  /**
   * Atualiza/insere metadados de objetos
   * 
   * @param items - Array de items para upsert
   * @returns Promise com resposta do upsert
   */
  static async upsert(items: UpsertItem[]): Promise<UpsertResponse> {
    debugLog('INFO', {
      scope: 'MetadataService.upsert',
      operation: 'upsert',
      itemCount: items.length
    });

    try {
      const response = await this.sendMetadataCommand<any>({
        operation: 'upsert',
        items
      });

      if (response.status === 'error' || response.status === 'erro') {
        throw new Error(response.message || 'Erro ao atualizar metadados');
      }

      const result = response.result;

      debugLog('INFO', {
        scope: 'MetadataService.upsert',
        success: true,
        updated: result.updated || 0,
        errors: result.errors?.length || 0
      });

      return {
        sucesso: result.sucesso !== false,
        updated: result.updated || 0,
        errors: result.errors || [],
        durationMs: result.durationMs
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      debugLog('ERROR', {
        scope: 'MetadataService.upsert',
        errorMessage
      });

      throw error;
    }
  }

  /**
   * Remove metadados de objetos
   * 
   * @param staticIds - StaticIDs dos objetos para remover metadados
   * @returns Promise com resposta da remoção
   */
  static async delete(staticIds: string[]): Promise<DeleteResponse> {
    debugLog('INFO', {
      scope: 'MetadataService.delete',
      operation: 'delete',
      count: staticIds.length
    });

    try {
      const response = await this.sendMetadataCommand<any>({
        operation: 'delete',
        staticIds
      });

      if (response.status === 'error' || response.status === 'erro') {
        throw new Error(response.message || 'Erro ao remover metadados');
      }

      const result = response.result;

      debugLog('INFO', {
        scope: 'MetadataService.delete',
        success: true,
        deleted: result.deleted || 0,
        errors: result.errors?.length || 0
      });

      return {
        sucesso: result.sucesso !== false,
        deleted: result.deleted || 0,
        errors: result.errors || [],
        durationMs: result.durationMs
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      debugLog('ERROR', {
        scope: 'MetadataService.delete',
        errorMessage
      });

      throw error;
    }
  }

  /**
   * Coleta snapshot completo de metadados (operation: 'full')
   * 
   * @param persistInStorage - Se deve persistir no storage (padrão: true)
   * @returns Promise com snapshot completo
   */
  static async collectFull(persistInStorage: boolean = true): Promise<FullMetadataResponse> {
    debugLog('INFO', {
      scope: 'MetadataService.collectFull',
      operation: 'full'
    });

    try {
      const response = await this.sendMetadataCommand<any>({
        operation: 'full'
      });

      if (response.status === 'error' || response.status === 'erro') {
        throw new Error(response.message || 'Erro ao coletar metadados completos');
      }

      // Persistir no storage se solicitado
      if (persistInStorage) {
        try {
          storage.set(Keys.export.lastMetadataSnapshot, response, {
            area: 'session',
            ttlMs: 10 * 60 * 1000 // 10 minutos
          });
        } catch (storageError) {
          debugLog('WARN', {
            scope: 'MetadataService.collectFull',
            message: 'Falha ao persistir no storage',
            error: storageError
          });
        }
      }

      const result = response.result;
      
      // Para operation: "full", os dados estão em result.snapshot
      const snapshot = result.snapshot || result;
      const objetos = snapshot.objetos || result.objetos || [];
      const paginas = snapshot.paginas || result.paginas || [];
      const documento = snapshot.documento || result.documento;

      debugLog('INFO', {
        scope: 'MetadataService.collectFull',
        success: true,
        hasSnapshot: !!result.snapshot,
        totalObjetos: objetos.length,
        totalPaginas: paginas.length,
        fonte: result.snapshot ? 'result.snapshot ✅' : 'result (fallback)'
      });

      return {
        sucesso: result.sucesso !== false,
        objetos: objetos,
        paginas: paginas,
        totalObjetos: objetos.length,
        totalPaginas: paginas.length,
        durationMs: result.durationMs,
        documento: documento,
        snapshot: result.snapshot // Preservar snapshot completo
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      debugLog('ERROR', {
        scope: 'MetadataService.collectFull',
        errorMessage
      });

      throw error;
    }
  }

  /**
   * Coleta metadados com retry automático
   * 
   * @param maxRetries - Número máximo de tentativas (padrão: 3)
   * @param retryDelay - Delay entre tentativas em ms (padrão: 500)
   * @returns Promise com resultado da coleta
   */
  static async collectFullWithRetry(
    maxRetries: number = DEFAULT_MAX_RETRIES,
    retryDelay: number = DEFAULT_RETRY_DELAY
  ): Promise<MetadataCollectionResult> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const currentTimeout = getMetadataBaseTimeout() + (attempt - 1) * METADATA_TIMEOUT_INCREMENT;
      
      try {
        sendToTerminal(`🧩 [METADATA] Coletando metadados (tentativa ${attempt}/${maxRetries})`, {
          timeout: currentTimeout
        });
        
        const result = await this.collectFull(true);
        
        sendToTerminal('✅ [METADATA] Metadados coletados com sucesso', { attempt });
        
        return {
          ok: true,
          payload: result,
          attempts: attempt
        };
        
      } catch (metaError) {
        sendToTerminal(`⚠️ [METADATA] Falha ao coletar metadados (tentativa ${attempt})`, {
          error: metaError instanceof Error ? metaError.message : String(metaError)
        });
      }
      
      // Delay antes do próximo retry (exceto na última tentativa)
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, retryDelay));
      }
    }
    
    return {
      ok: false,
      error: `Falha ao coletar metadados após ${maxRetries} tentativas`,
      attempts: maxRetries
    };
  }

  /**
   * Extrai objetos dos metadados
   * 
   * @param metadata - Metadados coletados
   * @returns Array de objetos ou array vazio
   */
  static extractObjects(metadata: any): ObjetoCorel[] {
    if (!metadata) return [];
    
    const result = metadata.result || metadata;
    if (!result) return [];
    
    return Array.isArray(result.objetos) ? result.objetos : [];
  }

  /**
   * Extrai páginas dos metadados
   * 
   * @param metadata - Metadados coletados
   * @returns Array de páginas ou array vazio
   */
  static extractPages(metadata: any): PaginaCorel[] {
    if (!metadata) return [];
    
    const result = metadata.result || metadata;
    if (!result) return [];
    
    return Array.isArray(result.paginas) ? result.paginas : [];
  }

  /**
   * Valida metadados coletados
   * 
   * @param metadata - Metadados a validar
   * @returns true se válidos, false caso contrário
   */
  static validate(metadata: any): boolean {
    if (!metadata) return false;
    
    const result = metadata.result || metadata;
    if (!result) return false;
    
    // Validar estrutura mínima
    if (result.sucesso === false) return false;
    if (!Array.isArray(result.objetos)) return false;
    
    return true;
  }

  /**
   * Envia comando de metadados para o CorelDRAW
   *
   * @param payload - Payload do comando
   * @param timeoutMs - Timeout em milissegundos (padrão: 2 minutos)
   * @param onProgress - Callback opcional para receber atualizações de progresso
   * @returns Promise com resposta
   * @private
   *
   * Suporta mensagens de progresso do C#:
   * - status: "progress_update" → chama onProgress e reseta timeout
   * - status: "ok" ou sucesso: true → resolve a promise
   * - status: "error" ou sucesso: false → rejeita a promise
   */
  private static sendMetadataCommand<T>(
    payload: any,
    timeoutMs: number = getMetadataBaseTimeout(),
    onProgress?: (progress: MetadataProgress) => void
  ): Promise<T> {
    sendToTerminal('📤 [MetadataService:sendMetadataCommand] Preparando envio', {
      operation: payload.operation,
      timeoutMs,
      hasProgressCallback: !!onProgress
    }, LogCategory.BUSINESS);

    return new Promise((resolve, reject) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let hasResolved = false;
      let lastProgressAt = Date.now();
      const commandStartedAt = Date.now();
      let firstProgressShownAt: number | null = null;
      let lastProgressPercent = 0;

      // Função para criar/resetar o timeout
      const resetTimeout = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (!hasResolved) {
            hasResolved = true;
            const timeSinceLastProgress = Date.now() - lastProgressAt;
            sendToTerminal('❌ [MetadataService:sendMetadataCommand] TIMEOUT!', {
              operation: payload.operation,
              timeoutMs,
              timeSinceLastProgressMs: timeSinceLastProgress
            }, LogCategory.ERROR);
            debugLog('ERROR', {
              scope: 'MetadataService.sendMetadataCommand',
              error: 'Timeout ao aguardar resposta do CorelDRAW',
              payload: { operation: payload.operation },
              timeoutMs,
              timeSinceLastProgressMs: timeSinceLastProgress
            });
            reject(new Error(`Timeout (${timeoutMs}ms) ao aguardar resposta do CorelDRAW. Verifique se o CorelDRAW está respondendo.`));
          }
        }, timeoutMs);
      };

      // ✅ Função para resolver com delay mínimo (UX)
      const resolveWithMinDelay = (response: T) => {
        if (hasResolved) return;

        // Se nunca mostrou progresso, resolver imediatamente
        if (!firstProgressShownAt) {
          hasResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          resolve(response);
          return;
        }

        // Calcular tempo restante para manter progresso visível
        const timeSinceFirstProgress = Date.now() - firstProgressShownAt;
        const minTotalTime = PROGRESS_MIN_SHOW_TIME_MS + PROGRESS_MIN_CLOSE_DELAY_MS;
        const remainingTime = Math.max(0, minTotalTime - timeSinceFirstProgress);

        if (remainingTime > 0) {
          // Emitir 100% antes de fechar (se callback disponível)
          if (onProgress && lastProgressPercent < 100) {
            onProgress({
              percentComplete: 100,
              message: 'Finalizando...',
              current: 0,
              total: 0
            });
          }

          sendToTerminal('⏳ [MetadataService] Aguardando tempo mínimo de UX', {
            remainingTimeMs: remainingTime,
            timeSinceFirstProgressMs: timeSinceFirstProgress
          }, LogCategory.BUSINESS);

          // Aguardar tempo mínimo antes de resolver
          setTimeout(() => {
            if (hasResolved) return;
            hasResolved = true;
            if (timeoutId) clearTimeout(timeoutId);
            resolve(response);
          }, remainingTime);
        } else {
          // Tempo mínimo já passou, resolver imediatamente
          hasResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          resolve(response);
        }
      };

      // Iniciar timeout
      resetTimeout();

      try {
        sendToTerminal('📤 [MetadataService:sendMetadataCommand] Chamando sendActionGuarded...', {
          action: 'metadados-objetos'
        }, LogCategory.BUSINESS);

        const requestId = corelCommunication.sendActionGuarded(
          'metadados-objetos',
          payload,
          (response) => {
            // Verificar se é progress_update
            const status = (response as any)?.status;
            const isProgressUpdate = status === 'progress_update';

            if (isProgressUpdate) {
              // É uma atualização de progresso - NÃO resolver ainda
              lastProgressAt = Date.now();
              resetTimeout(); // Resetar timeout a cada progresso

              const progressData = (response as any)?.result || response;
              const progress: MetadataProgress = {
                percentComplete: progressData.percentComplete ?? 0,
                message: progressData.message || 'Processando...',
                current: progressData.current ?? 0,
                total: progressData.total ?? 0,
                label: progressData.label,
                action: progressData.action
              };

              // ✅ UX: Throttle inicial - só mostrar progresso após tempo mínimo
              const timeSinceStart = Date.now() - commandStartedAt;
              const shouldShowProgress = timeSinceStart >= PROGRESS_MIN_SHOW_TIME_MS || firstProgressShownAt !== null;

              if (shouldShowProgress) {
                // Marcar quando primeiro progresso foi mostrado
                if (firstProgressShownAt === null) {
                  firstProgressShownAt = Date.now();
                }
                lastProgressPercent = progress.percentComplete;

                sendToTerminal('📊 [MetadataService:sendMetadataCommand] Progress update', {
                  percentComplete: progress.percentComplete,
                  current: progress.current,
                  total: progress.total,
                  message: progress.message,
                  timeSinceStartMs: timeSinceStart
                }, LogCategory.BUSINESS);

                // Chamar callback de progresso se fornecido
                if (onProgress) {
                  try {
                    onProgress(progress);
                  } catch (err) {
                    sendToTerminal('⚠️ [MetadataService] Erro no callback de progresso', {
                      error: err instanceof Error ? err.message : String(err)
                    }, LogCategory.ERROR);
                  }
                }
              } else {
                sendToTerminal('⏳ [MetadataService] Progress throttled (aguardando tempo mínimo)', {
                  percentComplete: progress.percentComplete,
                  timeSinceStartMs: timeSinceStart,
                  minShowTimeMs: PROGRESS_MIN_SHOW_TIME_MS
                }, LogCategory.BUSINESS);
              }
              return; // Não resolver - aguardar resposta final
            }

            // Resposta final (não é progress_update)
            if (hasResolved) return; // Ignorar se já resolveu (timeout)

            sendToTerminal('📥 [MetadataService:sendMetadataCommand] Callback recebido!', {
              hasResponse: !!response,
              responseStatus: status,
              hadProgressShown: firstProgressShownAt !== null
            }, LogCategory.BUSINESS);

            // ✅ UX: Usar resolveWithMinDelay para garantir tempo mínimo de exibição
            resolveWithMinDelay(response as T);
          }
        );

        sendToTerminal('📤 [MetadataService:sendMetadataCommand] sendActionGuarded retornou', {
          requestId: requestId || 'NULL'
        }, LogCategory.BUSINESS);

        if (!requestId) {
          if (hasResolved) return;
          hasResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          sendToTerminal('❌ [MetadataService:sendMetadataCommand] requestId é NULL!', {}, LogCategory.ERROR);
          reject(new Error('Falha ao enviar comando metadados-objetos'));
        }
      } catch (error) {
        if (hasResolved) return;
        hasResolved = true;
        if (timeoutId) clearTimeout(timeoutId);
        sendToTerminal('❌ [MetadataService:sendMetadataCommand] Exceção ao enviar', {
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
        reject(error);
      }
    });
  }
}

/**
 * Instância singleton exportada para uso direto
 * @deprecated Use a classe MetadataService diretamente
 */
export const metadataService = MetadataService;

// Re-exportar tipos para conveniência
export type { MetadataCollectionResult } from './metadata.types';

